import { useEffect, useState } from "react";
import {
  DialogTrigger,
  FullscreenDialog,
  Heading,
  Header,
  Content,
  ButtonGroup,
  Button,
  ActionButton,
  TextArea,
  Text,
  NotificationBadge,
} from "@react-spectrum/s2";
import SettingsIcon from "@react-spectrum/s2/icons/Settings";
import UndoIcon from "@react-spectrum/s2/icons/Undo";

import { useVideo, useVideoTemplate } from "@app/hooks/videos";
import { getCustomerBrandingPromptTemplate } from "@app/utils/systemPrompts";
import { getSystemPrompt } from "@app/services/zoltar";
import { useIsAdmin } from "@app/hooks/useRoles";

import { style } from "@react-spectrum/s2/style" with { type: "macro" };

export const EditPromptTemplateDialog = ({
  videoId,
  isOpen,
  getPromptTemplate,
  templateKey,
}) => {
  const video = useVideo(videoId);
  const videoTemplate = useVideoTemplate(video.data?.templateId);
  const isAdmin = useIsAdmin();
  const systemPromptId =
    getPromptTemplate?.(video.data)?.systemPromptId ??
    getCustomerBrandingPromptTemplate({
      templateId: video.data?.templateId,
      hint: "",
    }).systemPromptId;

  const _templateKey =
    templateKey ?? `customTemplate__${video.data?.templateId}`;

  const [_promptTemplate, setPromptTemplate] = useState();
  const [document, setDocument] = useState();
  const [loadError, setLoadError] = useState("");

  useEffect(() => {
    setDocument(undefined);
    setLoadError("");
    setPromptTemplate(undefined);
    if (!isAdmin || !isOpen) return;
    if (!systemPromptId) {
      setLoadError("No system prompt is configured for this template.");
      return;
    }
    const controller = new AbortController();
    setPromptTemplate(localStorage.getItem(_templateKey) ?? undefined);
    getSystemPrompt(systemPromptId, { signal: controller.signal })
      .then((result) => {
        if (!controller.signal.aborted) setDocument(result);
      })
      .catch((error) => {
        if (!controller.signal.aborted) setLoadError(error.message);
      });
    return () => controller.abort();
  }, [isAdmin, isOpen, systemPromptId, _templateKey]);

  const defaultPromptTemplate = document?.prompt ?? "";
  const promptTemplate = _promptTemplate ?? defaultPromptTemplate;

  const isModified = promptTemplate !== defaultPromptTemplate;

  const hasCustomTemplate = !!localStorage.getItem(_templateKey);

  const handleSave = (text, close) => {
    if (text === defaultPromptTemplate) localStorage.removeItem(_templateKey);
    else localStorage.setItem(_templateKey, text);
    close();
  };

  const handleCancel = (close) => {
    setPromptTemplate(defaultPromptTemplate);
    close();
  };

  const handleReset = (close) => {
    localStorage.removeItem(_templateKey);
    setPromptTemplate(defaultPromptTemplate);
    close();
  };

  if (!isAdmin) return null;

  return (
    <FullscreenDialog isOpen={isOpen}>
      {({ close }) => (
        <>
          <Heading slot="title">Edit prompt template</Heading>
          <Header>{videoTemplate.data?.name ?? video.data?.templateId}</Header>
          <Content>
            {loadError ? <Text>{loadError}</Text> : !document && <Text>Loading system prompt...</Text>}
            <TextArea
              aria-label="Edit prompt template"
              value={promptTemplate}
              onChange={setPromptTemplate}
              isDisabled={!document || !!loadError}
            />
          </Content>
          <ButtonGroup>
            {!!hasCustomTemplate && (
              <ActionButton
                onPress={() => handleReset(close)}
                variant="secondary"
                isQuiet
                isDisabled={!document || !!loadError}
              >
                <UndoIcon />
                <Text>Reset to default</Text>
              </ActionButton>
            )}
            <Button onPress={() => handleCancel(close)} variant="secondary">
              Cancel
            </Button>
            <Button
              onPress={() => handleSave(promptTemplate, close)}
              variant="accent"
              isDisabled={!document || !!loadError || !isModified || !promptTemplate.trim()}
            >
              Save Changes
            </Button>
          </ButtonGroup>
        </>
      )}
    </FullscreenDialog>
  );
};

export const EditPromptTemplateButton = ({
  videoId,
  getPromptTemplate,
  templateKey,
  ...props
}) => {
  const video = useVideo(videoId);
  const _templateKey =
    templateKey ?? `customTemplate__${video.data?.templateId}`;
  const hasCustomTemplate = !!localStorage.getItem(_templateKey);

  const [isOpen, setIsOpen] = useState(false);
  return (
    <DialogTrigger onOpenChange={setIsOpen}>
      <ActionButton aria-label="Edit prompt settings" {...props}>
        <SettingsIcon />
        {hasCustomTemplate && (
          <NotificationBadge
            size="L"
            UNSAFE_className={style({ backgroundColor: "notice" })}
          />
        )}
      </ActionButton>
      <EditPromptTemplateDialog
        videoId={videoId}
        getPromptTemplate={getPromptTemplate}
        templateKey={templateKey}
        isOpen={isOpen}
      />
    </DialogTrigger>
  );
};
